import { useEffect, useRef, useState } from 'react';
import { ErrorState, Loading, Notice, PageIntro } from '../components/ui';

/**
 * So funktioniert Hugarheim: the living technical documentation.
 *
 * The content is one committed, self-contained HTML file
 * (src/docs/how-hugarheim-works.html) that also opens on its own in a browser.
 * It is loaded lazily so it stays out of the main bundle, and shown in a sandboxed
 * iframe so its styles cannot touch the app. The file carries no script; the
 * sandbox grants same-origin access only so this page can size the frame and
 * scroll to anchors. It sits behind the app's login like every other page.
 *
 * The file records the release it was last verified against. When the deployed
 * release differs, the page says so instead of silently presenting stale detail.
 */

/** Reads the release the document was last verified against. */
export function docVerifiedVersion(html: string): string | null {
  const m = html.match(/<meta\s+name="hugarheim-doc-verified"\s+content="([^"]*)"/i);
  const v = m?.[1]?.trim().replace(/^v/, '');
  return v ? v : null;
}

/**
 * A warning when the deployed release is not the one the document was verified
 * against; null for a local build or a match.
 */
export function docStaleness(docVersion: string | null, appVersion: string | undefined): string | null {
  const app = (appVersion ?? '').trim().replace(/^v/, '');
  if (!app || app === 'dev') return null;
  if (!docVersion) return 'Diese Seite nennt keine geprüfte Version.';
  if (docVersion === app) return null;
  return `Diese Seite wurde zuletzt gegen v${docVersion} geprüft; ausgeliefert ist v${app}. Einzelne Angaben können veraltet sein.`;
}

/** Marks the document as embedded and applies the app's deliberate theme choice. */
export function prepareEmbedded(html: string, theme: string | undefined): string {
  const attrs = theme === 'light' || theme === 'dark' ? ` data-embedded data-theme="${theme}"` : ' data-embedded';
  return html.replace(/<html([^>]*)>/i, `<html$1${attrs}>`);
}

export function HowItWorksPage() {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [theme, setTheme] = useState<string | undefined>(() => document.documentElement.dataset.theme);
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let alive = true;
    import('../docs/how-hugarheim-works.html?raw')
      .then((m) => alive && setHtml(m.default))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e : new Error('Laden fehlgeschlagen')));
    return () => {
      alive = false;
    };
  }, []);

  // Follow the theme toggle in the app shell.
  useEffect(() => {
    const root = document.documentElement;
    const obs = new MutationObserver(() => setTheme(root.dataset.theme));
    obs.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const doc = frame.current?.contentDocument;
    if (!doc?.documentElement) return;
    if (theme === 'light' || theme === 'dark') doc.documentElement.dataset.theme = theme;
    else delete doc.documentElement.dataset.theme;
  }, [theme]);

  const onLoad = () => {
    const el = frame.current;
    const doc = el?.contentDocument;
    if (!el || !doc) return;
    const fit = () => {
      el.style.height = `${doc.documentElement.scrollHeight}px`;
    };
    fit();
    new ResizeObserver(fit).observe(doc.body);
    // The frame never scrolls itself, so in-page anchors scroll this page instead.
    doc.addEventListener('click', (ev) => {
      const a = (ev.target as Element | null)?.closest?.('a[href^="#"]');
      if (!a) return;
      const target = doc.getElementById(a.getAttribute('href')!.slice(1));
      if (!target) return;
      ev.preventDefault();
      const top = el.getBoundingClientRect().top + window.scrollY + target.getBoundingClientRect().top;
      window.scrollTo({ top: top - 12, behavior: 'smooth' });
    });
  };

  const stale = html ? docStaleness(docVerifiedVersion(html), import.meta.env.VITE_APP_VERSION as string | undefined) : null;

  return (
    <>
      <PageIntro eyebrow="Unter der Haube" title="So funktioniert Hugarheim">
        Wie Meldungen gesammelt, versioniert, zusammengeführt, gespeichert und dargestellt werden, und welche Arbeit der Code
        und welche das lokale Sprachmodell übernimmt. Dazu die beschlossenen nächsten Schritte.
      </PageIntro>
      {stale && <Notice kind="warn" title="Möglicherweise veraltet">{stale}</Notice>}
      {error && <ErrorState error={error} />}
      {!html && !error && <Loading label="Lädt die Dokumentation…" />}
      {html && (
        <iframe
          ref={frame}
          title="So funktioniert Hugarheim"
          className="doc-frame"
          sandbox="allow-same-origin"
          srcDoc={prepareEmbedded(html, theme)}
          onLoad={onLoad}
        />
      )}
    </>
  );
}
